import * as actionType from "../store/action";

export const EMAIL_VERIFIED_STORAGE_KEY = "emailVerified";
export const PENDING_VERIFICATION_KEY = "pendingEmailVerification";
export const EMAIL_VERIFICATION_DISMISSED_KEY = "emailVerificationDismissed";
const RESEND_SESSION_KEY = "emailVerificationResent";

export const isGuestRole = () => {
  try {
    return localStorage.getItem("userRole")?.includes("guest") ?? false;
  } catch {
    return false;
  }
};

export const isAdminRole = () => {
  try {
    const roles = JSON.parse(localStorage.getItem("userRole") || "[]");
    return roles.some((role) => role !== "customer" && role !== "guest");
  } catch {
    return false;
  }
};

export const getStoredEmailVerified = () => {
  try {
    const value = localStorage.getItem(EMAIL_VERIFIED_STORAGE_KEY);
    if (value === null) return true;
    return value !== "false";
  } catch {
    return true;
  }
};

export const setStoredEmailVerified = (verified) => {
  try {
    if (verified === false) {
      localStorage.setItem(EMAIL_VERIFIED_STORAGE_KEY, "false");
      return;
    }
    localStorage.removeItem(EMAIL_VERIFIED_STORAGE_KEY);
  } catch {
    /* ignore */
  }
};

export const isEmailVerificationDismissedInStorage = () => {
  try {
    return sessionStorage.getItem(EMAIL_VERIFICATION_DISMISSED_KEY) === "true";
  } catch {
    return false;
  }
};

export const setEmailVerificationDismissedInStorage = (dismissed) => {
  try {
    if (dismissed) {
      sessionStorage.setItem(EMAIL_VERIFICATION_DISMISSED_KEY, "true");
      return;
    }
    sessionStorage.removeItem(EMAIL_VERIFICATION_DISMISSED_KEY);
  } catch {
    /* ignore */
  }
};

export const persistPendingVerification = (payload) => {
  try {
    sessionStorage.setItem(PENDING_VERIFICATION_KEY, JSON.stringify(payload));
  } catch {
    /* ignore */
  }
};

export const getPersistedPendingVerification = () => {
  try {
    const raw = sessionStorage.getItem(PENDING_VERIFICATION_KEY);
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
};

export const clearEmailVerificationStorage = () => {
  try {
    localStorage.removeItem(EMAIL_VERIFIED_STORAGE_KEY);
    sessionStorage.removeItem(RESEND_SESSION_KEY);
    sessionStorage.removeItem(PENDING_VERIFICATION_KEY);
    sessionStorage.removeItem(EMAIL_VERIFICATION_DISMISSED_KEY);
  } catch {
    /* ignore */
  }
};

export const isProfileEmailVerified = (profile = {}) =>
  profile.emailVerified !== false;

export const hasPendingEmailVerification = () => {
  if (isGuestRole() || isAdminRole()) return false;
  if (!localStorage.getItem("jwtToken")) return false;
  return !getStoredEmailVerified();
};

export const shouldRequireEmailVerification = ({
  profile,
  emailVerifiedFromLogin,
} = {}) =>
  hasPendingEmailVerification() ||
  emailVerifiedFromLogin === false ||
  (profile && profile.emailVerified === false);

export const buildVerificationPayload = ({
  customerId,
  email,
  password = null,
  passwordMail = null,
  firstName = null,
  lastName = null,
}) => ({
  customerId,
  email,
  password,
  passwordMail,
  firstName,
  lastName,
});

export const getCustomerHeaderDisplayName = (user) => {
  if (user?.firstName) {
    return user.firstName;
  }

  const pending = getPersistedPendingVerification();
  if (pending?.firstName) {
    return pending.firstName;
  }

  return null;
};

export const applyDismissedEmailVerificationIfNeeded = (dispatch) => {
  if (!isEmailVerificationDismissedInStorage()) {
    return;
  }

  dispatch({
    type: actionType.SET_EMAIL_VERIFICATION_DISMISSED,
    payload: true,
  });
  dispatch({
    type: actionType.USER_ENTRY,
    payload: [false, "login"],
  });
};

export const dispatchPendingEmailVerification = (
  dispatch,
  payload,
  { resend = false, resendThunk } = {},
) => {
  setStoredEmailVerified(false);
  setEmailVerificationDismissedInStorage(false);
  persistPendingVerification(payload);
  dispatch({
    type: actionType.VERIFIED_EMAIL,
    payload,
  });

  if (!resend || !resendThunk) {
    return Promise.resolve();
  }

  if (sessionStorage.getItem(RESEND_SESSION_KEY)) {
    return Promise.resolve();
  }

  sessionStorage.setItem(RESEND_SESSION_KEY, "1");
  return dispatch(resendThunk({ suppressAlert: true })).catch(() => {
    sessionStorage.removeItem(RESEND_SESSION_KEY);
  });
};

export const restorePendingEmailVerification = (
  dispatch,
  existingPending = false,
) => {
  if (!hasPendingEmailVerification()) {
    return false;
  }

  if (existingPending) {
    applyDismissedEmailVerificationIfNeeded(dispatch);
    return true;
  }

  const pending =
    getPersistedPendingVerification() ||
    buildVerificationPayload({
      email: localStorage.getItem("lastLoginEmail") || "",
    });

  dispatch({
    type: actionType.VERIFIED_EMAIL,
    payload: pending,
  });
  applyDismissedEmailVerificationIfNeeded(dispatch);
  return true;
};

export const needsEmailVerification = (state) => hasPendingEmailVerification();

export const isEmailVerificationPopupOpen = (state) =>
  Boolean(state?.user?.userHaveReset) &&
  state?.user?.emailVerificationDismissed !== true;

export const promptEmailVerification = (dispatch, state) => {
  if (!needsEmailVerification(state)) {
    return false;
  }

  if (state?.user?.userHaveReset) {
    dispatch({
      type: actionType.SET_EMAIL_VERIFICATION_DISMISSED,
      payload: false,
    });
    dispatch({
      type: actionType.USER_ENTRY,
      payload: [false, "login"],
    });
    return true;
  }

  const pending =
    getPersistedPendingVerification() ||
    buildVerificationPayload({
      email: localStorage.getItem("lastLoginEmail") || "",
    });

  dispatch({
    type: actionType.VERIFIED_EMAIL,
    payload: pending,
  });
  return true;
};

export const guardCustomerAction = (dispatch, state) => {
  if (isAdminRole()) {
    return false;
  }

  if (needsEmailVerification(state)) {
    promptEmailVerification(dispatch, state);
    return true;
  }

  const hasJwt = Boolean(localStorage.getItem("jwtToken"));

  if (!hasJwt || isGuestRole()) {
    dispatch({ type: actionType.USER_ENTRY, payload: [true, "login"] });
    return true;
  }

  if (!state?.user?.user) {
    dispatch({ type: actionType.USER_ENTRY, payload: [true, "login"] });
    return true;
  }

  return false;
};

export const dismissEmailVerificationFlow = (dispatch) => {
  setEmailVerificationDismissedInStorage(true);
  dispatch({
    type: actionType.SET_EMAIL_VERIFICATION_DISMISSED,
    payload: true,
  });
  dispatch({
    type: actionType.USER_ENTRY,
    payload: [false, "login"],
  });
};
